import React from "react";
import { Batch, JobPipelineStepDetail, SoftwareEnumSchema, StatusEnumSchema } from "@domain/schemas/BI";
import { Button, Chip } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { useTranslation } from "react-i18next";
import { formatStepName } from "@presentation/components/job/JobList";

export type FailedBatchLogEntry = {
  step: Pick<JobPipelineStepDetail, "id" | "type">;
  batch: Pick<Batch, "id" | "status">;
};

type FailedBatchesDialogProperties = {
  entries: FailedBatchLogEntry[];
  /**
   * Engine the job runs on; required so step labels match the grid and the step view.
   */
  software?: SoftwareEnumSchema | null;
  isLoading: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onOpenLogs: (entry: FailedBatchLogEntry) => void;
};

const getStatusChipThemeColor = (status: StatusEnumSchema) => (status === StatusEnumSchema.Aborted ? "error" : "warning");

export const FailedBatchesDialog: React.FC<FailedBatchesDialogProperties> = ({ entries, software, isLoading, errorMessage, onClose, onOpenLogs }) => {
  const { t: loc } = useTranslation();

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 16 }}>
          <Loader size="small" type="infinite-spinner" />
          <span>{loc("loadingFailedBatchLogs")}</span>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div role="alert" style={{ padding: 16, color: "#b42318" }}>
          {errorMessage}
        </div>
      );
    }

    if (entries.length === 0) {
      return <div style={{ padding: 16 }}>{loc("noFailedBatchLogsResolved")}</div>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 6, overflow: "hidden" }}>
        {entries.map((entry) => (
          <div key={`${entry.step.id}-${entry.batch.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f1f1f1" }}>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{formatStepName(entry.step.type, software)}</span>
              <span style={{ fontSize: 12, color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={entry.batch.id}>
                {loc("batch")}: {entry.batch.id}
              </span>
            </div>
            <Chip size="small" themeColor={getStatusChipThemeColor(entry.batch.status)} selected={false} style={{ flexShrink: 0 }}>
              {entry.batch.status}
            </Chip>
            <Button fillMode="link" themeColor="secondary" style={{ padding: 0, flexShrink: 0 }} onClick={() => onOpenLogs(entry)}>
              {loc("logs")}
            </Button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog title={loc("failedBatchLogsTitle")} onClose={onClose} width={720}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 240, maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ fontSize: 13, color: "#5f5f5f" }}>{loc("failedBatchLogsArchivedLogCaveat")}</div>
        {renderContent()}
      </div>
    </Dialog>
  );
};
